import React, { ReactElement, useEffect, useState, useMemo, useCallback } from "react";
import {
    QualityHubListsOverviewBarProps,
    QualityHubListsOverviewBarState,
    QualityHubListsOverviewBarStateProps,
    UnitsResponse,
} from "./QualityHubListsOverviewBar.types";
import { getCancelTokenSource } from "src/api";
import api from "src/spintr/SpintrApi";
import { useSelector } from "react-redux";
import { isHubAdministrator } from "src/redux";
import { ActionMenu } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { SmallBody } from "../Text";

/**
 * QualityHubListsOverviewBar component
 *
 * @param {QualityHubListsOverviewBarProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubListsOverviewBar({
    onNodeChanged,
}: QualityHubListsOverviewBarProps): ReactElement | null {
    const {
        hubAdministrator,
        lastFetchForNodeId,
        userDepartmentId,
        userOfficeId
    } = useSelector<Spintr.AppState, QualityHubListsOverviewBarStateProps>(
        (appState) => ({
            hubAdministrator: isHubAdministrator(appState),
            lastFetchForNodeId: appState.qualityHub.listsFetchedForUberId,
            userDepartmentId: appState.profile.active.department?.id,
            userOfficeId: appState.profile.active.department?.office?.id,
        })
    );

    const [state, setState] = useState<QualityHubListsOverviewBarState>({
        offices: [],
        selectedNodeId: lastFetchForNodeId,
    });

    useEffect(() => {
        const cancelTokenSource = getCancelTokenSource();

        api.get<UnitsResponse>("/api/v1/units?take=9999", {
                cancelToken: cancelTokenSource.token,
                params: {
                    onlyPopulated: true,
                },
            })
            .then((response) => setState((prevState) => ({
                ...prevState,
                offices: response.data.offices,
            })))
            .catch((error) => {
                console.error(error);
            });

            return () => {
                cancelTokenSource.cancel("Component unmounted");
            };
        }, []);

        const categories = useMemo(
            () => {
                let offices = (state.offices || []).filter(
                    (office) => (office.departments || []).length > 0,
                );

                if (!hubAdministrator) {
                    offices = offices.filter((office) => office.id === userOfficeId);
                }

                if (offices.length === 0) {
                    return [];
                }

                const collator = typeof Intl !== "undefined" && Intl.Collator
                    ? new Intl.Collator("sv", {
                        usage: "sort",
                        sensitivity: "accent",
                        numeric: true,
                    })
                    : null;

                const sortFunction = collator !== null
                    ? (a: string, b: string) => collator.compare(a, b)
                    : (a: string, b: string) => a.localeCompare(b);

                return offices
                    .sort((a, b) => sortFunction(a.name, b.name))
                    .map((office) => ({
                        key: office.id,
                        title: office.name,
                        items: (office.departments || [])
                            .sort((a, b) => sortFunction(a.name, b.name))
                            .map((dept) => ({
                                key: dept.id,
                                text: dept.name,
                                onClick: () => {
                                    setState((prevState) => ({
                                        ...prevState,
                                        selectedNodeId: dept.id,
                                    }));
                                    onNodeChanged(dept.id);
                                },
                            })),
                    }));

            },
            [state.offices, hubAdministrator, userOfficeId]);

        const onRenderFilterButton = useCallback(() => {
            const deptId = state.selectedNodeId ?? userDepartmentId;
            const selectedDept = state.offices
                .flatMap((office) => office.departments || [])
                .find((dept) => dept.id === deptId);

            return (
                <div className="QualityHubListsOverviewBar-filterButton">
                    <div className="QualityHubListsOverviewBar-filterButton-left">
                        <Visage2Icon icon="sort" color="mid-grey" />
                    </div>
                    <div className="QualityHubListsOverviewBar-filterButton-right">
                        <SmallBody color="contentNormal" className="no-user-select">
                            {selectedDept?.name}
                        </SmallBody>
                        <div className="helper" />
                    </div>
                </div>
            );
        }, [state.offices, state.selectedNodeId, userDepartmentId]);

        return (
            <div className="QualityHubListsOverviewBar">
                <div className="QualityHubListsOverviewBar-right">
                    <ActionMenu categories={categories}
                                disableHoverEffect={true}
                                gapSpace={20}
                                onMenuDismissed={() => {}}
                                renderButton={onRenderFilterButton} />
                </div>
            </div>
        );
    }

    export default QualityHubListsOverviewBar;

